import React, { useMemo } from 'react';

import TabContainer, { Tab } from '~components/TabContainer';

import { CarePlan } from '~clients/models';

import { CARE_PLAN_TAB_LABELS, CARE_PLAN_TYPES } from '~clients/config/carePlanTypes';

import { CARE_PLAN_TAB_PANELS } from './CarePlanTabPanels';

interface ComponentProps {
  plan: CarePlan;
  activeTab: number;
  onTabChange: (index: number) => void;
}

/**
 * Tab shell for the selected care plan. Visible tabs come from the plan-type
 * config (CARE_PLAN_TYPES), never hardcoded. Each tab renders an interim
 * read-only panel that fetches the plan's sub-entities; the full tab UIs are
 * delivered by the per-tab stories.
 */
const CarePlanTabs: React.FC<ComponentProps> = ({ plan, activeTab, onTabChange }) => {
  const visibleTabs = CARE_PLAN_TYPES[plan.plan_type].tabs;

  // Selection can move to a plan type with fewer tabs (sah_restorative has 4 vs 6)
  // between the parent's reset and this render — clamp to a valid index.
  const clampedActiveTab = Math.min(activeTab, visibleTabs.length - 1);

  // Panels stay mounted while hidden, so pass each its active state (lets a tab clear
  // transient UI on navigate-away). Rebuilt on tab change so isActive stays current.
  const tabs = useMemo<Tab[]>(
    () =>
      visibleTabs.map((tab, index) => {
        const Panel = CARE_PLAN_TAB_PANELS[tab];

        return {
          id: `care-plan-${tab.replace(/_/g, '-')}`,
          title: CARE_PLAN_TAB_LABELS[tab],
          content: <Panel plan={plan} isActive={clampedActiveTab === index} />,
        };
      }),
    [plan, visibleTabs, clampedActiveTab],
  );

  return (
    <TabContainer
      className="care-plan-tabs"
      tabs={tabs}
      activeTab={clampedActiveTab}
      onTabChange={onTabChange}
      setPageTitle={false}
    />
  );
};

export default CarePlanTabs;
